import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import dayjs from 'dayjs';
import React, { useContext } from 'react';
import { AccountType } from '../../../../__generated__/pge-types';
import { useTranslation } from '../../../../hooks/useTranslation';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import colors from '../../../../themes/main-colors';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import { ResidentialsOutageState } from '../../types/types';
import CottageIcon from '../icons/CottageIcon';
import HomeWorkIcon from '../icons/HomeWorkIcon';
import { NestedOutage } from '../nested-outage';
import { NestedOutageStatusAccordion } from '../nested-outage/NestedOutageStatusAccordion';
import { OutageStatusGraphic } from '../outage-status-graphic';
import { PSPSInfo } from '../psps-info';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    expandedContainer: {
      display: 'flex',
      flexDirection: 'column',
      borderBottom: `1px solid ${colors.grey2}`,
      backgroundColor: colors.white,
    },
    accordion: {
      '& .MuiAccordionSummary-root': {
        alignItems: 'start',
        padding: 0,
      },
      '& .MuiAccordionSummary-content': {
        margin: 0,
      },
      '& .MuiAccordionSummary-expandIcon': {
        marginTop: 5,
        marginRight: -32,
        right: 25,
      },
    },
    collapsedContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1.25, 0),
      textAlign: 'center',
      cursor: 'pointer',
    },
    iconCenter: {
      alignSelf: 'center',
    },
    accordionDetails: {
      padding: 0,
      flexDirection: 'column',
    },
    accordionContainer: {
      padding: theme.spacing(3.75, 2.5),
    },
    accordionTriggerContent: {
      width: '100%',
    },
    noteBox: {
      backgroundColor: '#F5F5F5',
      borderRadius: 10,
      padding: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    smallText: {
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    fontBold: {
      fontWeight: 600,
    },
    statusLabel: {
      width: '55%',
    },
    statusValue: {
      width: '45%',
    },
    account: {
      fontSize: 18,
      fontWeight: 600,
    },
    location: {
      fontSize: 16,
    },
    restorationStatus: {
      cursor: 'pointer',
      textDecoration: 'underline',
      color: colors.sparkBlue,
    },
  }),
);

interface SingleAccountOutageDetailsProps {
  residentialOutageState: ResidentialsOutageState;
}

export const SingleAccountOutageDetails: React.FC<SingleAccountOutageDetailsProps> = ({
  residentialOutageState,
}) => {
  const classes = useStyles();
  const {
    panelOpen: expanded,
    selectedAccountForOutage,
    setPspsDetails,
  } = useOutageLeftPanelState();
  const { t } = useTranslation();
  const { isNestedOutageEnabled, loading: featureFlagLoading } = useContext(
    featureFlagsContext,
  );
  const selectedOutagePremise =
    selectedAccountForOutage?.premiseInfos?.[0].servicePointDetails?.[0]
      ?.outageInfo;

  return expanded ? (
    <Box className={classes.expandedContainer}>
      <PSPSInfo />
      {!featureFlagLoading && isNestedOutageEnabled && (
        <OutageStatusGraphic
          step={residentialOutageState?.nestedOutage ?? 0}
          hasKnownOutage={selectedOutagePremise?.hasKnownOutage}
        />
      )}
      {residentialOutageState?.nestedOutage ? (
        <NestedOutage step={residentialOutageState?.nestedOutage} />
      ) : (
        <>
          {selectedAccountForOutage?.hasKnownOutage ? (
            <NestedOutageStatusAccordion
              location={selectedAccountForOutage?.addressLocation}
              nestedOutageStatus={{
                estimatedRestoration: selectedOutagePremise?.estimatedTimeOn
                  ? dayjs(
                      selectedOutagePremise?.estimatedTimeOn,
                      'MMMM D, YYYY h:mm a',
                    )
                      .format('MMM D, h:mm a')
                      .replace(/AM/gi, 'a.m.')
                      .replace(/PM/gi, 'p.m.')
                  : t('TO_BE_DETERMINED'),
                restorationStatus: selectedOutagePremise?.eventStatus ?? '',
                customersAffected:
                  selectedOutagePremise?.customersAffected ?? 0,
                outageIdentified: selectedOutagePremise?.estimatedTimeOut
                  ? dayjs(
                      selectedOutagePremise?.estimatedTimeOut,
                      'MMMM D, YYYY h:mm a',
                    )
                      .format('MMM D, h:mm a')
                      .replace(/AM/gi, 'a.m.')
                      .replace(/PM/gi, 'p.m.')
                  : t('TO_BE_DETERMINED'),
                causeOfOutage: selectedOutagePremise?.cause ?? '',
              }}
              triggerContent={
                <Box display={'flex'} flexDirection={'column'} gridGap={20}>
                  <Box display={'flex'} alignItems={'center'} gridGap={10}>
                    {selectedAccountForOutage.accountType ===
                    AccountType.Com ? (
                      <HomeWorkIcon color={colors.roseRed} />
                    ) : (
                      <CottageIcon
                        width={32}
                        height={32}
                        color={colors.roseRed}
                      />
                    )}
                    <Box>
                      {selectedAccountForOutage?.isPrimaryAccount && (
                        <Typography variant="body2">{t('PRIMARY')}</Typography>
                      )}
                      <Typography variant="h6" className={classes.account}>
                        {selectedAccountForOutage?.accountNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }
            />
          ) : (
            <Box
              display={'flex'}
              flexDirection={'column'}
              gridGap={20}
              paddingY={3.75}
              paddingX={2.5}
            >
              <Box display={'flex'} alignItems={'center'} gridGap={10}>
                <CottageIcon
                  width={32}
                  height={32}
                  color={
                    selectedAccountForOutage?.hasKnownOutage
                      ? colors.roseRed
                      : colors.pacificBlue
                  }
                />
                <Box>
                  {selectedAccountForOutage?.isPrimaryAccount && (
                    <Typography variant="body2">{t('PRIMARY')}</Typography>
                  )}
                  <Typography variant="h6" className={classes.account}>
                    {selectedAccountForOutage?.accountNumber}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" className={classes.location}>
                {selectedAccountForOutage?.addressLocation}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  ) : (
    <Box className={classes.collapsedContainer}>
      <div className={classes.iconCenter}>
        <CottageIcon
          width={30}
          height={30}
          color={
            selectedAccountForOutage?.hasKnownOutage
              ? colors.roseRed
              : colors.pacificBlue
          }
        />
      </div>
      <Typography className={classes.smallText}>
        {t('ACCOUNT_LABEL')}
      </Typography>
    </Box>
  );
};
